// ============================================================
// TechStack.jsx
//
// Showcases the platform's course offerings and tech stack
// in a visually branded grid of clickable course cards.
//
// Sections:
//   - Hero headline + tagline badge
//   - Course category cards grouped by discipline
//   - Each card navigates to /course-page on click
// ============================================================

import React from "react";
import { styled } from "@mui/material/styles";
import { Typography, Grid, Box, Container, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Fade } from "react-awesome-reveal";

// ── MUI Icons (replacing react-icons for brand consistency) ──
import HtmlIcon from "@mui/icons-material/Html";
import JavascriptIcon from "@mui/icons-material/Javascript";
import CodeIcon from "@mui/icons-material/Code";
import CloudIcon from "@mui/icons-material/Cloud";
import StorageIcon from "@mui/icons-material/Storage";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BrushIcon from "@mui/icons-material/Brush";
import BuildIcon from "@mui/icons-material/Build";
import DevicesIcon from "@mui/icons-material/Devices";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TerminalIcon from "@mui/icons-material/Terminal";
import BugReportIcon from "@mui/icons-material/BugReport";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DnsIcon from "@mui/icons-material/Dns";
import MemoryIcon from "@mui/icons-material/Memory";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — centralized for easy rebranding.
 */
const BRAND = {
    dark:   "#100720",
    gold:   "#E5C95F",
    yellow: "#FFF19B",
    white:  "#FFFFFF",
};

/**
 * TECH_CATEGORIES
 * Groups of course cards displayed on the page.
 * Each item has a label, MUI icon, and accent color
 * matching the course identity from UserDashboard.
 */
const TECH_CATEGORIES = [
    {
        title: "Languages",
        items: [
            { label: "Java Developer",       icon: <CodeIcon />,        color: "#f44336" },
            { label: "Python Developer",     icon: <DataObjectIcon />,  color: "#1976d2" },
            { label: "C++ Developer",        icon: <MemoryIcon />,      color: "#9c27b0" },
            { label: "C# Developer",         icon: <BuildIcon />,       color: "#4caf50" },
            { label: "TypeScript Developer", icon: <CodeIcon />,        color: "#ff9800" },
            { label: "Javascript",           icon: <JavascriptIcon />,  color: "#fbc02d" },
        ],
    },
    {
        title: "Frontend & Design",
        items: [
            { label: "HTML5 & CSS",  icon: <HtmlIcon />,    color: "#e34f26" },
            { label: "React / Angular", icon: <DevicesIcon />, color: "#61dafb" },
        ],
    },
    {
        title: "Backend & Frameworks",
        items: [
            { label: "Spring Boot", icon: <AccountTreeIcon />, color: "#009688" },
            { label: "Express.js",  icon: <TerminalIcon />,    color: "#795548" },
        ],
    },
    {
        title: "Databases",
        items: [
            { label: "MongoDB",  icon: <StorageIcon />, color: "#43a047" },
            { label: "MySQL",    icon: <DnsIcon />,     color: "#2196f3" },
            { label: "Postgres", icon: <StorageIcon />, color: "#673ab7" },
        ],
    },
    {
        title: "DevOps & Tools",
        items: [
            { label: "Github",        icon: <AccountTreeIcon />, color: "#212121" },
            { label: "Linux",         icon: <TerminalIcon />,    color: "#607d8b" },
            { label: "Embedded IoT",  icon: <MemoryIcon />,      color: "#e91e63" },
            { label: "Software Testing", icon: <BugReportIcon />, color: "#ff5722" },
        ],
    },
];

/**
 * TAGLINE_ITEMS
 * Three pillars shown in the hero badge.
 */
const TAGLINE_ITEMS = [
    { icon: <BrushIcon sx={{ fontSize: 18 }} />,         label: "Training Material" },
    { icon: <CodeIcon sx={{ fontSize: 18 }} />,          label: "Hands-on Support" },
    { icon: <RocketLaunchIcon sx={{ fontSize: 18 }} />,  label: "Project Deployment" },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * CourseCard
 * Clickable white card for each course item.
 * Lifts on hover with a dark border accent.
 */
const CourseCard = styled(Box)({
    backgroundColor: BRAND.white,
    padding: "28px 20px",
    borderRadius: "16px",
    border: "1px solid #f0f0f0",
    textAlign: "center",
    transition: "all 0.3s ease",
    cursor: "pointer",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    "&:hover": {
        transform: "translateY(-8px)",
        borderColor: BRAND.dark,
        boxShadow: "0 20px 25px -5px rgba(16,7,32,0.12)",
    },
});

/**
 * TaglineBadge
 * Dark pill badge in the hero section showing the
 * three platform pillars.
 */
const TaglineBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    gap: "16px",
    backgroundColor: BRAND.dark,
    color: BRAND.white,
    padding: "12px 28px",
    borderRadius: "100px",
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.5px",
    boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
    flexWrap: "wrap",
    justifyContent: "center",
});

/**
 * BadgeDivider
 * Subtle vertical separator between tagline items.
 */
const BadgeDivider = styled(Box)({
    width: "1px",
    height: "20px",
    backgroundColor: "rgba(255,255,255,0.2)",
});

/**
 * CategoryLabel
 * Section heading for each course group.
 */
const CategoryLabel = styled(Typography)({
    fontWeight: 800,
    color: BRAND.dark,
    opacity: 0.6,
    letterSpacing: "2px",
    textTransform: "uppercase",
    fontSize: "0.78rem",
    marginBottom: "16px",
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function TechStack() {
    const navigate = useNavigate();

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <Container
            maxWidth="lg"
            sx={{
                py: 8,
                backgroundColor: "inherit",
                textAlign: "center",
                borderRadius: "15px",
            }}
        >
            {/* ── Hero: Section Label + Headline + Badge ── */}
            <Fade direction="down" triggerOnce>

                {/* Section overline */}
                <Typography
                    variant="overline"
                    sx={{
                        fontSize: { xs: "0.75rem", md: "0.85rem" },
                        fontWeight: 800,
                        color: BRAND.dark,
                        letterSpacing: "3px",
                        display: "block",
                        mb: 1,
                    }}
                >
                    OUR COURSES & WHAT WE OFFER
                </Typography>

                {/* Hero headline */}
                <Typography
                    variant="h3"
                    sx={{
                        fontSize: { xs: "1.8rem", md: "2.5rem" },
                        fontWeight: 900,
                        color: BRAND.dark,
                        fontFamily: "'Georgia', serif",
                        mb: 3,
                        lineHeight: 1.2,
                    }}
                >
                    16 Professional{" "}
                    <Box component="span" sx={{ color: "#b8860b" }}>
                        Course Tracks
                    </Box>
                </Typography>

                {/* Tagline badge */}
                <TaglineBadge>
                    {TAGLINE_ITEMS.map((item, index) => (
                        <React.Fragment key={item.label}>
                            {index > 0 && <BadgeDivider />}
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box sx={{ color: BRAND.gold }}>{item.icon}</Box>
                                <Typography variant="body2" fontWeight={600}>
                                    {item.label}
                                </Typography>
                            </Box>
                        </React.Fragment>
                    ))}
                </TaglineBadge>

            </Fade>

            {/* ── Course Category Sections ── */}
            {TECH_CATEGORIES.map((category, categoryIndex) => (
                <Fade
                    key={category.title}
                    direction="up"
                    delay={categoryIndex * 100}
                    triggerOnce
                >
                    <Box sx={{ mt: 6, mb: 2, textAlign: "left" }}>

                        {/* Category heading */}
                        <CategoryLabel>{category.title}</CategoryLabel>

                        <Grid container spacing={3}>
                            {category.items.map((item) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={item.label}>

                                    {/* Course Card */}
                                    <CourseCard onClick={() => navigate("/course-page")}>

                                        {/* Colored icon container */}
                                        <Box
                                            sx={{
                                                width: 56, height: 56,
                                                borderRadius: "14px",
                                                backgroundColor: `${item.color}18`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                "& svg": {
                                                    fontSize: 28,
                                                    color: item.color,
                                                },
                                            }}
                                        >
                                            {item.icon}
                                        </Box>

                                        {/* Course title */}
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            color={BRAND.dark}
                                        >
                                            {item.label}
                                        </Typography>

                                        {/* View syllabus link */}
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                            sx={{ color: item.color }}
                                        >
                                            <Typography variant="caption" fontWeight={600}>
                                                View Syllabus
                                            </Typography>
                                            <ArrowForwardIcon sx={{ fontSize: 14 }} />
                                        </Box>

                                    </CourseCard>

                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Fade>
            ))}

            {/* ── Bottom Course Count Summary ── */}
            <Fade direction="up" delay={600} triggerOnce>
                <Box
                    display="flex"
                    justifyContent="center"
                    flexWrap="wrap"
                    gap={1.5}
                    mt={6}
                >
                    {[
                        "16 Course Tracks",
                        "Inbuilt Compiler",
                        "DSA Training",
                        "Certification",
                        "Project Deployment",
                    ].map((label) => (
                        <Chip
                            key={label}
                            label={label}
                            sx={{
                                backgroundColor: BRAND.dark,
                                color: BRAND.gold,
                                fontWeight: 600,
                                fontSize: "0.78rem",
                            }}
                        />
                    ))}
                </Box>
            </Fade>

        </Container>
    );
}
